import clsx from 'clsx';
import { DashboardBodyLayout } from 'components/Layout/DashboardBodyLayout';
import React from 'react';

import { Badge } from '@fragment/ui/src/components/Badge/Badge';
import { Icon } from '@fragment/ui/src/components/Icon';
import { AccountTypeIcon } from '@fragment/ui/src/components/Icon/AccountTypeIcon/AccountTypeIcon';
import { EntryIcon } from '@fragment/ui/src/components/Icon/EntryIcon/EntryIcon';
import { List, ListItem, Marker } from '@fragment/ui/src/components/List';
import type { ColumnDefinition } from '@fragment/ui/src/components/Table';
import { Table } from '@fragment/ui/src/components/Table';
import { POSTHOG_MASK_CLASS } from '@fragment/ui/src/constants';

import { exampleAccountRows, exampleEntries } from './data';

const entryColumns: readonly ColumnDefinition<
  (typeof exampleEntries)[number]
>[] = [
  {
    title: 'Entries',
    key: 'id',
    colspan: 'col-span-4',
    format: ({ id, accountTypes }) => (
      <Badge badge={<EntryIcon types={accountTypes} />}>{id}</Badge>
    ),
  },
  {
    title: 'Count',
    key: 'count',
    justify: 'right',
    colspan: 'col-span-1',
  },
];

export const ExampleLedgerDashboard = () => (
  <div className="relative flex h-screen flex-col overflow-x-hidden bg-canvas text-main text-xmd">
    <header className="relative z-20 box-border flex h-f6 w-full min-w-0 shrink-0 flex-row items-center gap-f1 border-b-[0.5px] border-main-300 bg-canvas px-[2ch]">
      <Icon type="workspace" size="sm" />
      <span className="text-main-300">/</span>
      <span>Example project</span>
      <span className="text-main-300">/</span>
      <span>Sandbox ledger</span>
    </header>
    <DashboardBodyLayout
      isLeftNavExpanded
      navigation={
        <nav aria-label="Ledger navigation">
          <List type="ordered">
            {['Overview', 'Accounts', 'Entries', 'Details'].map(
              (label, index) => (
                <ListItem key={label} extraClassNames="min-w-f0 w-fit">
                  <span
                    className={clsx(
                      'flex flex-row items-start min-w-f0 w-full',
                      index === 0 ? 'text-main' : 'text-main-500'
                    )}
                  >
                    <Marker
                      type="numeric"
                      ordinal={index}
                      margin="mr-[1ch]"
                      flexItem="shrink-0"
                    />
                    <span className="min-w-f0 w-full truncate">{label}</span>
                  </span>
                </ListItem>
              )
            )}
          </List>
        </nav>
      }
      navigationFooter={
        <Icon type="sidebar" size="sm" primaryColor="gray500" />
      }
    >
      <div className="h-full w-full pl-f2 pt-f2">
        <div className="mb-f4">
          <p className="text-main-500">Example ledger</p>
          <h1 className="mt-f1">Overview</h1>
        </div>
        <div className="grid grid-cols-1 gap-y-f2 laptop:grid-cols-2 gap-x-f2">
          <section aria-label="Balances" className={POSTHOG_MASK_CLASS}>
            <div className="flex w-full justify-between text-main-500">
              <span>Accounts</span>
              <span>Balance</span>
            </div>
            <dl className="flex h-fit flex-col border-t-[0.5px] border-main-300">
              {exampleAccountRows.map(
                ({ account, amount, type, label, branch, isRoot }) => (
                  <div
                    key={account}
                    className="flex h-f2 items-center justify-between gap-x-[1ch] overflow-x-hidden border-[0.5px] border-t-0 border-main-300 bg-canvas"
                  >
                    <dt
                      aria-label={account}
                      className="flex min-w-0 items-center whitespace-nowrap"
                    >
                      {isRoot ? (
                        <AccountTypeIcon type={type} />
                      ) : (
                        <span
                          aria-hidden="true"
                          className="whitespace-pre text-main-500"
                        >
                          {branch}
                        </span>
                      )}
                      {label}
                    </dt>
                    <dd className="ml-auto whitespace-nowrap">{amount}</dd>
                  </div>
                )
              )}
            </dl>
          </section>
          <section aria-label="Entries">
            <Table
              textSize="md"
              schema={entryColumns}
              data={exampleEntries}
              stickyHeader={false}
              dottedHeader={false}
              borderedRows
              disableRowHover
            />
          </section>
        </div>
      </div>
    </DashboardBodyLayout>
  </div>
);
